/**
 * Клиентский OCR через локальный PaddleOCR (ONNX Web Worker).
 * Модели и runtime: /vendor/paddleocr/ (закрытый контур, без CDN).
 *
 * Важно: PaddleOCR.js всегда качает asset как .tar и ищет внутри
 * `inference.onnx` / `inference.yml` — URL обязан указывать на .tar, не на папку.
 */

export interface OCRBlock {
  text: string;
  confidence: number;
}

export interface OCRResult {
  text: string;
  confidence: number;
  blocks: OCRBlock[];
  source: "paddleocr";
  durationMs: number;
}

const PADDLE_MODULE_PATH = "/vendor/paddleocr/index.mjs";
const MODEL_BASE = "/vendor/paddleocr/models";
/** Путь к WASM onnxruntime-web (локальная копия, без jsDelivr). */
const WASM_PATHS = "/vendor/paddleocr/onnxruntime/";

const DET_MODEL_TAR = `${MODEL_BASE}/PP-OCRv5_mobile_det_onnx_infer.tar`;
const REC_MODEL_TAR = `${MODEL_BASE}/eslav_PP-OCRv5_mobile_rec_onnx_infer.tar`;

type OcrPredictInstance = {
  predict: (src: Blob | File | HTMLImageElement | string) => Promise<unknown[]>;
};

type PaddleOCRModule = {
  PaddleOCR?: { create: (opts: Record<string, unknown>) => Promise<OcrPredictInstance> };
};

let ocrInstance: OcrPredictInstance | null = null;
let initPromise: Promise<OcrPredictInstance> | null = null;

/**
 * Vite запрещает `import` файлов из `/public` в исходниках (они копируются as-is).
 * Грузим native ESM по абсолютному origin-URL вне графа Vite.
 */
async function loadPaddleOCRModule(): Promise<PaddleOCRModule> {
  const href = new URL(PADDLE_MODULE_PATH, window.location.origin).href;
  // new Function: Vite не статически резолвит аргумент import() в publicDir
  const dynamicImport = new Function("u", "return import(u)") as (u: string) => Promise<PaddleOCRModule>;
  return dynamicImport(href);
}

function resetOcrSingleton(): void {
  ocrInstance = null;
  initPromise = null;
}

async function getOCRInstance(): Promise<OcrPredictInstance> {
  if (ocrInstance) return ocrInstance;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const mod = await loadPaddleOCRModule();
    const PaddleOCRClass = mod.PaddleOCR;
    if (!PaddleOCRClass?.create) {
      throw new Error("PaddleOCR class not found in /vendor/paddleocr/index.mjs");
    }

    const instance = await PaddleOCRClass.create({
      textDetectionModelName: "PP-OCRv5_mobile_det",
      textDetectionModelAsset: { url: DET_MODEL_TAR },
      textRecognitionModelName: "eslav_PP-OCRv5_mobile_rec",
      textRecognitionModelAsset: { url: REC_MODEL_TAR },
      // Worker иначе падает на CDN onnxruntime-web — в закрытом контуре нужен локальный путь
      ortOptions: { wasmPaths: WASM_PATHS },
      initialize: true,
      worker: true,
    });

    ocrInstance = instance;
    return instance;
  })().catch((err) => {
    resetOcrSingleton();
    throw err;
  });

  return initPromise;
}

export async function recognizeText(
  imageSource: Blob | File | HTMLImageElement | string,
): Promise<OCRResult> {
  const startTime = performance.now();
  const ocr = await getOCRInstance();

  const results = (await ocr.predict(imageSource)) as Array<{
    items?: Array<{ text?: string; score?: number }>;
  }>;
  const durationMs = Math.round(performance.now() - startTime);

  const allBlocks: OCRBlock[] = [];
  let fullText = "";
  let totalScore = 0;
  let count = 0;

  for (const result of results || []) {
    for (const item of result.items || []) {
      const trimmed = (item.text || "").trim();
      if (trimmed) {
        const score = typeof item.score === "number" ? item.score : 0;
        allBlocks.push({ text: trimmed, confidence: score });
        fullText += trimmed + "\n";
        totalScore += score;
        count++;
      }
    }
  }

  return {
    text: fullText.trim(),
    confidence: count > 0 ? totalScore / count : 0,
    blocks: allBlocks,
    source: "paddleocr",
    durationMs,
  };
}

export function isOCRQualityLow(result: OCRResult): boolean {
  return result.text.length < 50 || result.confidence < 0.5;
}
